import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { getCurrencyRates } from "@/services/currencyService";

// Fallback static kurlar (Supabase başarısız olursa kullanılır)
const STATIC_CURRENCIES = {
  GBP: { symbol: "£", code: "GBP", rate: 1 },
  USD: { symbol: "$", code: "USD", rate: 1.27 },
  EUR: { symbol: "€", code: "EUR", rate: 1.17 },
  TRY: { symbol: "₺", code: "TRY", rate: 41.5 },
  RUB: { symbol: "₽", code: "RUB", rate: 113.5 },
  AED: { symbol: "AED", code: "AED", rate: 4.67 },
};

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currencies, setCurrencies] = useState(STATIC_CURRENCIES);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrencyState] = useState(() => {
    const stored = localStorage.getItem("oly_currency");
    return STATIC_CURRENCIES[stored] || STATIC_CURRENCIES.GBP;
  });

  // Supabase'den kurları çek
  useEffect(() => {
    const fetchRates = async () => {
      try {
        setLoading(true);
        const rates = await getCurrencyRates();
        
        // Eğer Supabase'den veri geldiyse kullan
        if (rates && Object.keys(rates).length > 0) {
          setCurrencies(rates);
          
          // Seçili currency'yi güncelle
          const stored = localStorage.getItem("oly_currency");
          if (stored && rates[stored]) {
            setCurrencyState(rates[stored]);
          } else {
            setCurrencyState(rates.GBP || STATIC_CURRENCIES.GBP);
          }
        } else {
          // Veri boşsa fallback kullan
          console.warn('No currency data from Supabase, using static fallback');
          setCurrencies(STATIC_CURRENCIES);
        }
      } catch (error) {
        // Hata durumunda fallback kullan
        console.error('Failed to fetch currency rates, using static fallback:', error);
        setCurrencies(STATIC_CURRENCIES);
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []);

  const setCurrency = useCallback((code) => {
    const c = currencies[code];
    if (c) {
      setCurrencyState(c);
      localStorage.setItem("oly_currency", code);
    }
  }, [currencies]);

  const formatPrice = useCallback(
    (gbpAmount, locale = "en") => {
      const converted = Math.round(gbpAmount * currency.rate);
      try {
        return new Intl.NumberFormat(locale === "ar" ? "ar-SA" : locale === "ru" ? "ru-RU" : locale === "tr" ? "tr-TR" : "en-GB", {
          style: "currency",
          currency: currency.code,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(converted);
      } catch {
        return `${currency.symbol}${converted.toLocaleString()}`;
      }
    },
    [currency]
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, currencies, loading }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
